// Case notes as the reader meets them: short pills whose colour names the kind of finding, with
// the full account — which step, how it compares — in a tooltip. Shared by the Cases tab's table
// and the case view, so a case reads the same in both places.
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import type { CaseAnalysis, CaseNote, CaseNoteTone } from "./processCaseNotes";

const TONE_CLASS: Record<CaseNoteTone, string> = {
  delay: "bg-danger/10 text-danger-strong",
  attention: "bg-attention-soft text-warning-strong",
  variation: "bg-variation-soft text-variation",
  info: "bg-info/10 text-info-strong",
  neutral: "bg-surface-strong text-text-mid",
};

const DOT_CLASS: Record<CaseNoteTone, string> = {
  delay: "bg-danger",
  attention: "bg-warning",
  variation: "bg-variation",
  info: "bg-info",
  neutral: "bg-text-dim",
};

/** What the notes are read against: the scope's size and what a typical case looks like. */
export type CaseNoteContext = Pick<CaseAnalysis, "total"> & { typicalCaseSeconds: number };

export const caseNoteContext = (analysis: CaseAnalysis): CaseNoteContext => ({
  total: analysis.total,
  typicalCaseSeconds: analysis.profile.median,
});

type NoteText = { pill: string; title: string; body: string; context?: string };

export const useCaseNoteText = (nameById: Map<string, string>, context: CaseNoteContext) => {
  const { t } = useTranslation();
  return useCallback(
    (note: CaseNote): NoteText => {
      const step = (id: string) => nameById.get(id) ?? id;
      const typical = formatDuration(context.typicalCaseSeconds);
      switch (note.kind) {
        case "wait":
          return {
            pill: t("processPanel.pill_wait", { duration: formatDuration(note.seconds) }),
            title: t(
              note.tone === "delay"
                ? "processPanel.noteTitle_longWait"
                : "processPanel.noteTitle_wait",
            ),
            body: t("processPanel.noteBody_wait", {
              duration: formatDuration(note.seconds),
              step: step(note.beforeStepId),
            }),
            context: t(
              note.tone === "delay"
                ? "processPanel.noteContext_longWait"
                : "processPanel.noteContext_wait",
              { typical },
            ),
          };
        case "rework":
          return {
            pill:
              note.loops > 1
                ? t("processPanel.pill_reworkMany", { count: note.loops })
                : t("processPanel.pill_rework"),
            title: t("processPanel.noteTitle_rework"),
            body: t("processPanel.noteBody_rework", {
              count: note.loops,
              step: step(note.stepId),
            }),
            context:
              note.typical === 0
                ? t("processPanel.noteContext_reworkNone")
                : t("processPanel.noteContext_rework", { count: note.typical }),
          };
        case "variation":
          return {
            pill:
              note.more > 0
                ? t("processPanel.pill_variationMany", { count: note.more + 1 })
                : t("processPanel.pill_variation"),
            title: t("processPanel.noteTitle_variation"),
            body: t("processPanel.noteBody_variation", {
              step: step(note.stepId),
              count: note.cases,
              total: context.total,
            }),
            context:
              note.more > 0
                ? t("processPanel.noteContext_variation", { count: note.more })
                : undefined,
          };
        case "app":
          return {
            pill: t("processPanel.pill_app", { app: note.app }),
            title: t("processPanel.noteTitle_app"),
            body: t("processPanel.noteBody_app", {
              app: note.app,
              count: note.cases,
              total: context.total,
            }),
          };
        case "steps":
          return {
            pill: t("processPanel.pill_steps", { count: note.count }),
            title: t("processPanel.noteTitle_steps"),
            body: t("processPanel.noteBody_steps", { count: note.count, typical: note.typical }),
          };
      }
    },
    [context.total, context.typicalCaseSeconds, nameById, t],
  );
};

const NoteDetails = ({ tone, text }: { tone: CaseNoteTone; text: NoteText }) => (
  <div className="py-2.5 first:pt-0 last:pb-0">
    <p className="flex items-center gap-2 text-sm font-semibold text-text">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[tone]}`} />
      {text.pill}
    </p>
    <p className="mt-1 text-sm leading-relaxed text-text-mid">{text.body}</p>
    {text.context && <p className="mt-1.5 text-xs leading-relaxed text-text-dim">{text.context}</p>}
  </div>
);

const Pill = ({
  tone,
  text,
  details = [{ tone, text }],
}: {
  tone: CaseNoteTone;
  text: NoteText;
  details?: { tone: CaseNoteTone; text: NoteText }[];
}) => {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const content = (
    <div className="divide-y divide-outline">
      {details.map(detail => (
        <NoteDetails key={detail.text.title} {...detail} />
      ))}
    </div>
  );
  const contentClass =
    "pd-theme z-[60] w-80 max-w-[calc(100vw-32px)] max-h-[var(--radix-popover-content-available-height,80vh)] overflow-y-auto rounded-lg border border-outline bg-surface px-4 py-3 text-left shadow-lift";
  return (
    <Popover.Root open={pinned} onOpenChange={setPinned}>
      <Tooltip.Root open={!pinned && hovered} onOpenChange={setHovered}>
        <Popover.Trigger asChild>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              aria-label={text.pill.startsWith("+") ? text.title : text.pill}
              className={`inline-block max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium leading-5 outline-none transition hover:brightness-95 focus-visible:ring-2 focus-visible:ring-primary/30 ${TONE_CLASS[tone]}`}
            >
              {text.pill}
            </button>
          </Tooltip.Trigger>
        </Popover.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" sideOffset={6} collisionPadding={16} className={contentClass}>
            {content}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      <Popover.Portal>
        <Popover.Content
          aria-label={text.title}
          side="top"
          sideOffset={6}
          collisionPadding={16}
          onOpenAutoFocus={event => event.preventDefault()}
          className={contentClass}
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

/**
 * A case's notes, most notable first. A case with nothing to note says so in neutral grey —
 * leaving the cell blank would read as missing data rather than as an ordinary case.
 */
export const CaseNoteBadges = ({
  notes,
  nameById,
  context,
  limit = 2,
  className = "",
}: {
  notes: CaseNote[];
  nameById: Map<string, string>;
  context: CaseNoteContext;
  /** Pills shown before the rest collapse into "+N"; each hidden one is named in its tooltip. */
  limit?: number;
  className?: string;
}) => {
  const { t } = useTranslation();
  const text = useCaseNoteText(nameById, context);
  const visible = notes.slice(0, limit);
  const hidden = notes.slice(limit);
  const lastVisible = hidden.length > 0 ? visible.at(-1) : undefined;
  return (
    <Tooltip.Provider delayDuration={150}>
      <span className={`flex min-w-0 flex-wrap items-center gap-1.5 ${className}`}>
        {notes.length === 0 ? (
          <Pill
            tone="neutral"
            text={{
              pill: t("processPanel.note_standard"),
              title: t("processPanel.note_standard"),
              body: t("processPanel.noteBody_standard"),
            }}
          />
        ) : (
          (lastVisible ? visible.slice(0, -1) : visible).map(note => (
            <Pill key={note.kind} tone={note.tone} text={text(note)} />
          ))
        )}
        {hidden.length > 0 && (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
            {lastVisible && <Pill tone={lastVisible.tone} text={text(lastVisible)} />}
            <span className="inline-flex shrink-0">
              <Pill
                tone="neutral"
                text={{
                  pill: t("processPanel.noteMore", { count: hidden.length }),
                  title: t("processPanel.noteMoreLabel", { count: hidden.length }),
                  body: "",
                }}
                details={hidden.map(note => ({ tone: note.tone, text: text(note) }))}
              />
            </span>
          </span>
        )}
      </span>
    </Tooltip.Provider>
  );
};

/** The notes as one sentence each, for assistive tech where the pills sit inside a control. */
export const useCaseNoteDescription = (nameById: Map<string, string>, context: CaseNoteContext) => {
  const { t } = useTranslation();
  const text = useCaseNoteText(nameById, context);
  return useCallback(
    (notes: CaseNote[]) =>
      notes.length === 0
        ? t("processPanel.note_standard")
        : notes.map(note => `${text(note).pill}: ${text(note).body}`).join(" "),
    [t, text],
  );
};

/** Names what each note colour stands for, under the table that uses them. */
export const CaseNotesLegend = () => {
  const { t } = useTranslation();
  const tones = ["delay", "attention", "variation", "info"] as const;
  return (
    <div
      role="note"
      aria-label={t("processPanel.legendTitle")}
      className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-dim"
    >
      {tones.map(tone => (
        <span key={tone} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT_CLASS[tone]}`} />
          {t(`processPanel.legend_${tone}`)}
        </span>
      ))}
      <span>
        <span className="font-semibold text-danger-strong">
          {t("processPanel.legendDurationLabel")}
        </span>{" "}
        {t("processPanel.legendDuration")}
      </span>
    </div>
  );
};
